import * as Sentry from '@sentry/nextjs';
import { observer } from 'mobx-react-lite';
import { useSession } from 'next-auth/react';
import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { IFooter } from '@features/footer/typescript';
import { ENavTransparency, INavigation, INavParams } from '@features/navigation/typescript';
import { TCategoriesTree } from '@features/categories/typescript';
import layoutUiState from '@features/layout/mobX/LayoutUiState';
import { useCartStore } from '@features/cart/mobx/context/CartStoreProvider';
import { useCustomerDataStore } from '@features/customerData/mobx/context/CustomerDataStoreProvider';
import styles from '@features/layout/components/Layout/Layout.styles';
import { getRegion, removeConfiguratorScripts } from '@utils/misc';
import useAuth from '@hooks/useAuth';
import alertsStore from '@context/global/mobx/alertStore';
import Navigation from '@features/navigation/components/Navigation/Navigation';
import ReaffiliatesNewSignIn from '@reaffiliates/ReaffiliatesNewSignIn';
import SimpleLayout from '@features/layout/components/Layout/components/SimpleLayout/SimpleLayout';
import NavigationContainer from '@features/layout/components/Layout/components/NavigationContainer/NavigationContainer';
import Footer from '@features/footer/Footer';
import useBodyOverflow from '@hooks/useBodyOverflow';
import useServiceMap from '@hooks/useServiceMap';
import LangPopup from '@components/LangPopup/LangPopup';
import PromotionalMessages from '@components/PromotionalMessages/PromotionalMessages';
import { IAddressCollection } from '@features/checkout/typescript';
import { IAdditionalArgument } from '@features/cart/typescript';
import { useCartSync } from '@hooks/useCartSync';
import CmsPromotionalMessage from '@features/cms/objects/CmsPromotionalMessage';
import { ICmsPromotionalMessage } from '@features/cms/typescript';
import theme from '@styles/theme';
import { ROLES } from '@config/index';
import SentryToolbar from '@components/SentryToolbar/SentryToolbar';
import { getSentryToolbarConfig } from '@config/sentry-toolbar';
import GlobalTooltipProvider from '@components/GlobalTooltipProvider/GlobalTooltipProvider';
const DynamicCartMiniSideBar = dynamic(() => import('@features/cart/components/CartSideBar/CartMiniSideBar'), {
  ssr: false
});
const DynamicAlerts = dynamic(() => import('@components/Alerts/Alerts'), {
  ssr: false
});
const DynamicPopups = dynamic(() => import('@components/Popups/Popups'), {
  ssr: false
});
type PropsType = {
  children: React.ReactNode;
  footer?: IFooter;
  navigationBar?: INavigation;
  simpleLayout?: boolean;
  attachFirstChildToNavigation?: boolean;
  navParams?: INavParams;
  categoriesTree?: TCategoriesTree;
  promotionalMessages?: ICmsPromotionalMessage[];
  addresses?: IAddressCollection;
  overflowY?: string;
  overflowX?: string;
};
const Layout = ({
  children,
  footer = {} as IFooter,
  navigationBar = {} as INavigation,
  simpleLayout = false,
  attachFirstChildToNavigation = false,
  navParams = {
    navTransparency: ENavTransparency.BOTH_NOT_TRANSPARENT
  } as INavParams,
  categoriesTree = {} as TCategoriesTree,
  promotionalMessages = [],
  addresses = undefined as IAddressCollection | undefined,
  overflowY = undefined,
  overflowX = undefined
}: PropsType) => {
  useCartSync();
  const {
    data: session
  } = useSession();

  // Sprawdź czy użytkownik ma rolę ECOMMERCE_DEVELOPER
  const isEcommerceDeveloper = session?.user?.roles?.includes(ROLES.ECOMMERCE_DEVELOPER);
  const {
    navigationProductItemSelected,
    navigationAndFooterVisible,
    isCartCreated,
    setCartCreated,
    isMenuMobileOpened,
    isVideoModalOpened,
    isSearchOpened,
    userData,
    langPopupHeight,
    isPromotionalMessagesOpened,
    barPositionStatic,
    isMenuPined,
    isLoginGeneralOpened,
    userPanelOpened,
    setIsPromotionalMessagesOpened,
    setBarsPositionStatic,
    isProductCardOpened,
    navigationContainerIsStatic,
    setNavigationContainerIsStatic,
    navigationContainerIsVisible,
    setNavigationContainerIsVisible,
    setNavigationContainerHeight
  } = layoutUiState;
  const region = getRegion();
  const [langPopupIsActive, setLangPopupIsActive] = useState(false);
  const {
    cartId,
    fetchAndPopulateCartStore,
    fetchAvailableDeliveries,
    getCartFromLocalStorage
  } = useCartStore();
  const {
    pathname
  } = useRouter();
  const {
    id: customerID
  } = useCustomerDataStore();
  const {
    createSession
  } = useAuth();
  const serviceMap = useServiceMap(categoriesTree);
  const getPromotionalMessages = (): CmsPromotionalMessage[] => {
    if (promotionalMessages.length) {
      return promotionalMessages.map(promotionalMessage => new CmsPromotionalMessage(promotionalMessage));
    }
    return [];
  };
  const handleNewSignInGTM = () => {
    const delayMaxTime = 1000 * 10;
    const isWithinDelay = session?.token?.newSignIn && typeof session.token.newSignIn === 'number' && Date.now() - session.token.newSignIn < delayMaxTime;
    if (isWithinDelay) ReaffiliatesNewSignIn(customerID);
  };
  useEffect(() => {
    if (!pathname.includes('product')) {
      removeConfiguratorScripts();
    }
  }, [pathname]);
  useEffect(() => {
    if (session) {
      if (typeof customerID === 'string') handleNewSignInGTM();
      Sentry.configureScope(scope => {
        scope.setTag('customerId', customerID);
      });
    }
  }, [customerID]);
  useEffect(() => {
    const refetchAvailableDeliveries = async () => {
      if (isCartCreated && addresses) {
        let additionalArgument: undefined | IAdditionalArgument;
        if (region === 'MX' && addresses?.basicAddress?.postalCode) {
          additionalArgument = {
            key: 'postalCode',
            value: addresses?.basicAddress.postalCode
          };
        } else if (region === 'EUR' && addresses?.basicAddress?.country) {
          additionalArgument = {
            key: 'countryCode',
            value: addresses?.basicAddress.country
          };
        }
        await fetchAvailableDeliveries(additionalArgument);
      }
    };
    refetchAvailableDeliveries();
  }, [addresses]);
  useEffect(() => {
    const getCart = async () => {
      if (!getCartFromLocalStorage()) return;
      const {
        cartId: fetchedCartId,
        products
      } = await fetchAndPopulateCartStore();
      if (fetchedCartId && products.size > 0) {
        let additionalArgument: undefined | IAdditionalArgument;
        if (region === 'MX' && addresses?.basicAddress?.postalCode) {
          additionalArgument = {
            key: 'postalCode',
            value: addresses?.basicAddress.postalCode
          };
        } else if (region === 'EUR' && addresses?.basicAddress?.country) {
          additionalArgument = {
            key: 'countryCode',
            value: addresses?.basicAddress.country
          };
        }
        await fetchAvailableDeliveries(additionalArgument);
      }
      setCartCreated(true);
    };
    getCart();
  }, []);
  useEffect(() => {
    createSession();
  }, [session]);
  useEffect(() => {
    Sentry.configureScope(scope => {
      scope.setTag('cartId', cartId);
    });
  }, [cartId, langPopupHeight, langPopupIsActive]);
  useEffect(() => {
    setIsPromotionalMessagesOpened(getPromotionalMessages().length > 0 && !isMenuMobileOpened && !isMenuPined && !isLoginGeneralOpened && !userPanelOpened && !isProductCardOpened);
    setBarsPositionStatic(getPromotionalMessages().length > 0 && !isMenuMobileOpened && !isMenuPined && isProductCardOpened);
  }, [getPromotionalMessages().length, isMenuMobileOpened, isMenuPined, isLoginGeneralOpened, userPanelOpened, isProductCardOpened]);
  const isAlertVisible = useMemo(() => alertsStore.alerts.length > 0 && alertsStore.currentAlert, [alertsStore.alerts.length]);
  const fixedHeight = useMemo(() => !!(navigationProductItemSelected || isMenuMobileOpened || isSearchOpened || userData || isAlertVisible || isVideoModalOpened), [navigationProductItemSelected, isMenuMobileOpened, isSearchOpened, userData, isAlertVisible, isVideoModalOpened]);
  useBodyOverflow(fixedHeight, 'layout');
  const getPromotionalMessagesHeight = (): number => isPromotionalMessagesOpened ? 33 : 0;
  const getMarginTop = useMemo(() => {
    let marginTop = 0;
    if (isSearchOpened) return marginTop;
    if (langPopupIsActive && !isMenuMobileOpened && !isMenuPined && !isLoginGeneralOpened && !userPanelOpened && langPopupHeight && !barPositionStatic) marginTop += langPopupHeight;
    if (isPromotionalMessagesOpened && !barPositionStatic) marginTop += 33;
    return marginTop;
  }, [langPopupIsActive, langPopupHeight, barPositionStatic, isPromotionalMessagesOpened, isMenuMobileOpened, isMenuPined, isSearchOpened, isLoginGeneralOpened, userPanelOpened]);
  const footerHasKeys = useMemo(() => !!Object.keys(footer).length, [footer]);
  if (simpleLayout) {
    return <SimpleLayout attachFirstChildToNavigation={attachFirstChildToNavigation} overflowY={overflowY} overflowX={overflowX}>
        {children}
      </SimpleLayout>;
  }
  return <div sx={styles.layout(getMarginTop, overflowY || null, overflowX || null)}>
      {isCartCreated &&
    // @ts-expect-error - React 19 compatibility issue with next/dynamic types
    <DynamicCartMiniSideBar isCartCreated={isCartCreated} />}
      <PromotionalMessages isActive={(!!isPromotionalMessagesOpened || barPositionStatic) && !!navigationBar} messages={getPromotionalMessages()} />
      {navigationAndFooterVisible && navigationBar && <>
          <LangPopup active={langPopupIsActive && !isMenuMobileOpened && !isMenuPined} setActive={setLangPopupIsActive} promotionalMessagesHeight={getPromotionalMessagesHeight()} />
        </>}
      <NavigationContainer positionTop={getMarginTop} isProductCardOpened={isProductCardOpened} navigationContainerIsStatic={navigationContainerIsStatic} setNavigationContainerIsStatic={setNavigationContainerIsStatic} navigationContainerIsVisible={navigationContainerIsVisible} setNavigationContainerIsVisible={setNavigationContainerIsVisible} setNavigationContainerHeight={setNavigationContainerHeight} isMenuMobileOpened={isMenuMobileOpened} isMenuPined={isMenuPined} isSearchOpened={isSearchOpened} isLoginGeneralOpened={isLoginGeneralOpened} userPanelOpened={userPanelOpened}>
        <Navigation navElements={navigationBar} categoriesTree={categoriesTree} serviceMap={serviceMap} navParams={!navigationContainerIsStatic ? {
        navTransparency: ENavTransparency.BOTH_NOT_TRANSPARENT,
        backgroundColor: theme.rawColors.neutralWhite
      } as INavParams : navParams} />
      </NavigationContainer>
      {/* @ts-expect-error - React 19 compatibility issue with next/dynamic types */}
      <DynamicAlerts />
      {/* @ts-expect-error - React 19 compatibility issue with next/dynamic types */}
      <DynamicPopups />
      {children}
      {navigationAndFooterVisible && footerHasKeys && <Footer footer={footer} />}

      {/* Sentry Toolbar dla developerów */}
      {isEcommerceDeveloper && <SentryToolbar enabled={getSentryToolbarConfig().enabled} organizationSlug={getSentryToolbarConfig().organizationSlug} projectIdOrSlug={getSentryToolbarConfig().projectIdOrSlug} environment={getSentryToolbarConfig().environment} placement={getSentryToolbarConfig().placement} theme={getSentryToolbarConfig().theme} sentryOrigin={getSentryToolbarConfig().sentryOrigin} domId={getSentryToolbarConfig().domId} debug={getSentryToolbarConfig().debug} />}

      {/* Globalny Tooltip Provider */}
      <GlobalTooltipProvider />
    </div>;
};
export default observer(Layout);